import { Loader2, Download, Send, CheckCircle2, Eye, FilePlus2 } from "lucide-react";
import { money, computeTotals } from "../invoice-gen/totals";

function Row({ label, value }) {
  return (
    <div className="flex justify-between text-sm py-1">
      <span className="text-[#6B7280]">{label}</span>
      <span className="text-[#080808] font-medium text-right">{value || "—"}</span>
    </div>
  );
}

export default function InvoiceAiDraftPanel({
  draft,
  workflowStatus,
  readyToSend,
  sentInfo,
  onDownload,
  onSend,
  onPreview,
  onStartNewInvoice,
  resettingDraft,
  downloading,
  sending,
}) {
  const items = draft?.items || [];
  const totals = computeTotals(
    items.map((i) => ({ amount: i.amount, tax: i.tax })),
    draft?.payments || []
  );

  return (
    <div className="border border-[#E5E7EB] rounded-lg p-5 sticky top-20" data-testid="invoice-ai-draft-panel">
      <p className="text-xs uppercase tracking-wide text-[#3A7A8C] font-semibold mb-3">Live Draft</p>

      {onStartNewInvoice && (
        <button
          type="button"
          onClick={onStartNewInvoice}
          disabled={resettingDraft}
          data-testid="invoice-ai-start-new-invoice-button"
          title="Clear this draft and start billing a different client — keeps the chat history"
          className="mb-3 w-full inline-flex items-center justify-center gap-1.5 text-xs border border-dashed border-[#D1D5DB] rounded-md px-2.5 py-1.5 text-[#6B7280] hover:border-[#3A7A8C] hover:text-[#3A7A8C] disabled:opacity-60"
        >
          {resettingDraft ? <Loader2 size={12} className="animate-spin" /> : <FilePlus2 size={12} />} Start New Invoice
        </button>
      )}

      <Row label="Bill To" value={draft?.bill_to_name} />
      <Row label="Email" value={draft?.bill_to_email} />
      <Row label="Invoice #" value={draft?.invoice_number} />
      <Row label="Status" value={draft?.status} />

      <div className="border-t border-[#F3F4F6] mt-3 pt-3">
        <p className="text-xs text-[#6B7280] mb-2">Line Items ({items.length})</p>
        {items.length === 0 ? (
          <p className="text-xs text-[#9CA3AF]" data-testid="invoice-ai-no-items">No line items yet.</p>
        ) : (
          <div className="flex flex-col gap-2">
            {items.map((it, idx) => (
              <div key={idx} className="text-xs bg-[#F9FAFB] rounded p-2" data-testid={`invoice-ai-draft-item-${idx}`}>
                <p className="text-[#080808] font-medium">
                  {it.service_date || "—"} · {money(it.amount)}
                </p>
                <p className="text-[#6B7280] mt-0.5">
                  {it.service_description || it.notes || it.details || "No description"}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="border-t border-[#F3F4F6] mt-3 pt-3 text-sm">
        <div className="flex justify-between font-semibold text-[#080808]" data-testid="invoice-ai-draft-total">
          <span>Total</span>
          <span>{money(totals.total)}</span>
        </div>
      </div>

      {onPreview && (
        <button
          type="button"
          onClick={onPreview}
          data-testid="invoice-ai-preview-button"
          className="mt-3 w-full inline-flex items-center justify-center gap-1.5 text-xs border border-[#D1D5DB] rounded-md px-2.5 py-1.5 hover:border-[#3A7A8C] hover:text-[#3A7A8C]"
        >
          <Eye size={12} /> Preview Invoice
        </button>
      )}

      {workflowStatus === "generated" && !readyToSend && (
        <div className="mt-4 border border-[#DCFCE7] bg-[#F0FDF4] rounded-lg p-3" data-testid="invoice-ai-generated-banner">
          <p className="text-xs text-[#166534] flex items-center gap-1.5 font-medium">
            <CheckCircle2 size={13} /> Invoice generated
          </p>
          <button
            type="button"
            onClick={onDownload}
            disabled={downloading}
            data-testid="invoice-ai-download-button"
            className="mt-2 inline-flex items-center gap-1.5 text-xs text-[#3A7A8C] hover:text-[#080808] disabled:opacity-60"
          >
            {downloading ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} Download PDF
          </button>
        </div>
      )}

      {readyToSend && (
        <div className="mt-4 border border-[#5FA8B4]/40 bg-[#5FA8B4]/5 rounded-lg p-3" data-testid="invoice-ai-ready-to-send">
          <p className="text-xs uppercase tracking-wide text-[#3A7A8C] font-semibold mb-2">Ready to Send</p>
          <Row label="To" value={readyToSend.recipient_email} />
          <Row label="Total" value={money(readyToSend.total)} />
          <div className="flex gap-2 mt-2">
            <button
              type="button"
              onClick={onDownload}
              disabled={downloading}
              data-testid="invoice-ai-ready-download-button"
              className="inline-flex items-center gap-1.5 text-xs border border-[#D1D5DB] rounded-md px-2.5 py-1.5 hover:border-[#3A7A8C] disabled:opacity-60"
            >
              {downloading ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} PDF
            </button>
            <button
              type="button"
              onClick={onSend}
              disabled={sending}
              data-testid="invoice-ai-send-button"
              className="ash-btn-primary flex-1 inline-flex items-center justify-center gap-1.5 text-xs disabled:opacity-60"
            >
              {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Send Invoice
            </button>
          </div>
        </div>
      )}

      {workflowStatus === "sent" && (
        <div className="mt-4 border border-[#DCFCE7] bg-[#F0FDF4] rounded-lg p-3" data-testid="invoice-ai-sent-banner">
          <p className="text-xs text-[#166534] flex items-center gap-1.5 font-medium">
            <CheckCircle2 size={13} /> Invoice sent
          </p>
          {sentInfo?.recipient && (
            <p className="text-[11px] text-[#166534]/80 mt-1">
              To {sentInfo.recipient}
              {sentInfo.sentAt ? ` · ${new Date(sentInfo.sentAt).toLocaleString()}` : ""}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
